import {
  ClipboardList,
  Mic,
  Users,
  MessageSquareDashed,
  Calendar,
  FileText,
  BookHeart,
  Watch,
} from "lucide-react";

/**
 * The 8 research methodologies MediTalk offers.
 * Shared across the site so copy stays in sync everywhere.
 */
export const METHODOLOGIES = [
  {
    key: "online-surveys",
    icon: ClipboardList,
    title: "Online surveys",
    short: "Online surveys",
    time: "10–20 minutes",
    body: "Quick, structured questions you answer at your own pace, from your phone or laptop.",
  },
  {
    key: "one-to-one-interviews",
    icon: Mic,
    title: "One-to-one interviews",
    short: "1-on-1 interviews",
    time: "45–60 minutes",
    body: "A friendly Zoom call with a researcher who wants to understand your story. No right answers, no judgement.",
  },
  {
    key: "group-discussions",
    icon: Users,
    title: "Group discussions",
    short: "Group discussions",
    time: "60–90 minutes",
    body: "Join a small group of patients or caregivers to explore a topic together, guided by a researcher.",
  },
  {
    key: "online-discussion-boards",
    icon: MessageSquareDashed,
    title: "Online discussion boards",
    short: "Online discussion boards",
    time: "3–5 days, ~15 min/day",
    body: "Post short reflections over a few days alongside other participants. Async, no live calls, fits around real life.",
  },
  {
    key: "diaries-and-check-ins",
    icon: Calendar,
    title: "Diaries & daily check-ins",
    short: "Diaries & daily check-ins",
    time: "A few minutes per day",
    body: "Short daily entries that capture the rhythms of life with your condition, one day at a time.",
  },
  {
    key: "feedback-on-materials",
    icon: FileText,
    title: "Feedback on health materials",
    short: "Feedback on health materials",
    time: "20–40 minutes",
    body: "Review leaflets, websites, app designs or patient information, and tell researchers what actually helps.",
  },
  {
    key: "patient-journey",
    icon: BookHeart,
    title: "Sharing your patient journey",
    short: "Your patient journey",
    time: "60+ minutes",
    body: "A deeper, story-led conversation about your diagnosis, treatment and everything in between.",
  },
  {
    key: "wearables-and-apps",
    icon: Watch,
    title: "Wearable or app-based studies",
    short: "Wearables & app studies",
    time: "Days to weeks",
    body: "Use a simple wearable or app to capture everyday health data. Research you barely notice you're doing.",
  },
];

/**
 * Options for the admin "format" dropdown when creating / editing a study.
 * Uses `title` as the stored value so the same string renders everywhere.
 */
export const METHODOLOGY_OPTIONS = METHODOLOGIES.map((m) => m.title);

/**
 * Research types used when an admin creates a study , the multi-select
 * the admin picks from. Distinct from the 8 user-facing METHODOLOGIES
 * above (which power the "what research is" landing page).
 *
 * Studies can be tagged with multiple types (e.g. Online Interview +
 * Survey) and an "Other" with a free-text spec for one-off methodologies.
 */
export const RESEARCH_TYPES = [
  { key: "face_to_face_interview", label: "Face to Face Interview" },
  { key: "face_to_face_group", label: "Face to Face Group" },
  { key: "online_interview", label: "Online Interview" },
  { key: "online_group", label: "Online Group" },
  { key: "survey", label: "Survey" },
  { key: "online_bulletin_board", label: "Online Bulletin Board" },
  { key: "pre_task", label: "Pre-task" },
  { key: "post_task", label: "Post-task" },
  { key: "other", label: "Other, specify" },
];

/**
 * Confirmation of diagnosis / treatment , the three levels an admin
 * can choose from when describing how a study verifies participants.
 * The `key` is what we store in the DB (`diagnosis_confirmation`);
 * the `label` is the public-facing copy.
 */
export const DIAGNOSIS_CONFIRMATION_OPTIONS = [
  { key: "self_reported", label: "Screening questions" },
  { key: "self_plus_docs", label: "Screening questions + Documents held" },
  { key: "physician_crf", label: "Screening questions + Physician confirmation" },
];
